import React, { useEffect, useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Mail,
  Edit,
  Trash2,
  Shield,
  Search,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  UserCog,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  ShieldOff,
  KeyRound,
  Eye,
  EyeOff,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  // Mobilização
  liderancas: 'Lideranças',
  apoiadores: 'Apoiadores',
  links: 'Links',
  destinos: 'Destinos',
  cartoes: 'Cartões',
  ranking: 'Ranking',
  relatorios: 'Relatórios',
  // Financeiro
  financeiro_planejamento: 'Financeiro · Planejamento',
  financeiro_gastos: 'Financeiro · Gastos',
  financeiro_analise: 'Financeiro · Análise',
  financeiro_prestacao: 'Financeiro · Prestação de Contas',
  // Sistema
  configuracoes: 'Configurações',
  logs: 'Logs de Auditoria',
  usuarios: 'Usuários',
};

const LEVEL_LABELS = {
  none: 'Sem acesso',
  view: 'Visualizar',
  edit: 'Editar (criar/excluir)',
};

const LEVEL_COLORS = {
  none: 'text-muted-foreground',
  view: 'text-blue-700',
  edit: 'text-green-700',
};

const ROLE_LABELS = {
  admin: 'Administrador',
  coordenador: 'Coordenador',
  lideranca: 'Liderança',
  juridico: 'Jurídico',
};

const STATUS_LABELS = {
  active: { label: 'Ativo', icon: CheckCircle2, color: 'text-green-600' },
  pending: { label: 'Pendente', icon: Clock, color: 'text-yellow-600' },
  disabled: { label: 'Desativado', icon: XCircle, color: 'text-red-600' },
};

const PermissionsEditor = ({ permissions, onChange, disabled, sections, roleDefaults, currentRole }) => {
  return (
    <div className="space-y-2 border border-border rounded-sm p-4 bg-secondary/20">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-sm">Permissões por seção</h4>
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(roleDefaults[currentRole] || {})}
            data-testid="reset-permissions-btn"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Padrão do perfil
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {sections.map((section) => (
          <div
            key={section}
            className="flex items-center justify-between gap-3 py-1.5 px-2 hover:bg-muted/30 rounded-sm"
          >
            <Label className="text-sm font-medium flex-1">
              {SECTION_LABELS[section] || section}
            </Label>
            <Select
              value={permissions[section] || 'none'}
              onValueChange={(value) => onChange({ ...permissions, [section]: value })}
              disabled={disabled}
            >
              <SelectTrigger
                className={`w-44 h-8 text-xs ${LEVEL_COLORS[permissions[section] || 'none']}`}
                data-testid={`perm-${section}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem acesso</SelectItem>
                <SelectItem value="view">Visualizar</SelectItem>
                <SelectItem value="edit">Editar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
};

export const UsuariosPage = () => {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [permissionsConfig, setPermissionsConfig] = useState({ sections: [], levels: [], role_defaults: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetDialog, setResetDialog] = useState(null); // user to reset
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'coordenador',
    password: '',
    permissions: {},
    status: 'active',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, permsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/permissions/sections`, { withCredentials: true }),
      ]);
      setUsuarios(usersRes.data);
      setPermissionsConfig(permsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingUser(null);
    setShowPassword(false);
    setFormData({
      email: '',
      name: '',
      role: 'coordenador',
      password: '',
      permissions: permissionsConfig.role_defaults.coordenador || {},
      status: 'active',
    });
    setShowPermissions(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (user) => {
    setEditingUser(user);
    setShowPassword(false);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      password: '',
      permissions: user.permissions || permissionsConfig.role_defaults[user.role] || {},
      status: user.status,
    });
    setShowPermissions(true);
    setDialogOpen(true);
  };

  const handleRoleChange = (newRole) => {
    setFormData({
      ...formData,
      role: newRole,
      permissions: permissionsConfig.role_defaults[newRole] || {},
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const payload = {
          name: formData.name,
          role: formData.role,
          permissions: formData.permissions,
          status: formData.status,
        };
        await axios.put(`${BACKEND_URL}/api/users/${editingUser.id}`, payload, {
          withCredentials: true,
        });
        toast.success('Usuário atualizado com sucesso!');
      } else {
        if (!formData.password || formData.password.length < 8) {
          toast.error('Defina uma senha com pelo menos 8 caracteres');
          return;
        }
        const payload = {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          password: formData.password,
          permissions: formData.permissions,
        };
        await axios.post(`${BACKEND_URL}/api/users/invite`, payload, {
          withCredentials: true,
        });
        toast.success('Usuário criado com sucesso! Ele já pode fazer login.');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Erro ao salvar';
      toast.error(message);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordValue || resetPasswordValue.length < 8) {
      toast.error('Senha deve ter no mínimo 8 caracteres');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/users/${resetDialog.id}/reset-password`,
        { password: resetPasswordValue },
        { withCredentials: true }
      );
      toast.success(`Senha de ${resetDialog.name} redefinida com sucesso!`);
      setResetDialog(null);
      setResetPasswordValue('');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao redefinir senha');
    }
  };

  const generatePassword = () => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const nums = '23456789';
    const sym = '@#$&!';
    const all = upper + lower + nums + sym;
    const out = [
      upper[Math.floor(Math.random() * upper.length)],
      lower[Math.floor(Math.random() * lower.length)],
      nums[Math.floor(Math.random() * nums.length)],
      sym[Math.floor(Math.random() * sym.length)],
    ];
    for (let i = 0; i < 8; i++) out.push(all[Math.floor(Math.random() * all.length)]);
    return out.sort(() => Math.random() - 0.5).join('');
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Excluir ${user.name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/users/${user.id}`, {
        withCredentials: true,
      });
      toast.success('Usuário excluído com sucesso!');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao excluir');
    }
  };

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'disabled' : 'active';
    const verb = newStatus === 'active' ? 'reativar' : 'desativar';
    if (!window.confirm(`Tem certeza que deseja ${verb} ${user.name}?`)) return;
    try {
      await axios.put(`${BACKEND_URL}/api/users/${user.id}`, { status: newStatus }, {
        withCredentials: true,
      });
      toast.success(newStatus === 'active' ? 'Usuário reativado' : 'Usuário desativado');
      fetchData();
    } catch (err) {
      toast.error('Erro ao alterar status');
    }
  };

  const filtered = useMemo(() => {
    return usuarios.filter((u) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !u.name.toLowerCase().includes(term) &&
          !u.email.toLowerCase().includes(term)
        )
          return false;
      }
      if (filterRole && filterRole !== 'todos' && u.role !== filterRole) return false;
      if (filterStatus && filterStatus !== 'todos' && u.status !== filterStatus)
        return false;
      return true;
    });
  }, [usuarios, searchTerm, filterRole, filterStatus]);

  const canCreateAdmin =
    currentUser?.is_seed_admin ||
    currentUser?.role === 'admin' ||
    currentUser?.role === 'super_admin';
  const canEditUsers =
    currentUser?.role === 'super_admin' ||
    currentUser?.role === 'admin' ||
    currentUser?.permissions?.usuarios === 'edit';

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="usuarios-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight">Usuários</h1>
            <p className="text-muted-foreground mt-2">
              Gerencie usuários, perfis e permissões de acesso
            </p>
          </div>
          {canEditUsers && (
            <Button onClick={handleOpenCreate} data-testid="create-usuario-btn">
              <Plus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-usuarios"
                />
              </div>
              <Select
                value={filterRole || 'todos'}
                onValueChange={(v) => setFilterRole(v === 'todos' ? '' : v)}
              >
                <SelectTrigger data-testid="filter-role">
                  <SelectValue placeholder="Perfil" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos perfis</SelectItem>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filterStatus || 'todos'}
                onValueChange={(v) => setFilterStatus(v === 'todos' ? '' : v)}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos status</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="disabled">Desativados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary/30">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-bold">Usuário</th>
                  <th className="px-4 py-3 font-bold">Perfil</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold">Resumo de permissões</th>
                  <th className="px-4 py-3 font-bold text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const status = STATUS_LABELS[u.status] || STATUS_LABELS.active;
                  const StatusIcon = status.icon;
                  const editCount = Object.values(u.permissions || {}).filter(
                    (l) => l === 'edit'
                  ).length;
                  const viewCount = Object.values(u.permissions || {}).filter(
                    (l) => l === 'view'
                  ).length;
                  return (
                    <tr
                      key={u.id}
                      className="border-b border-border hover:bg-muted/30 transition-colors"
                      data-testid={`usuario-row-${u.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Shield className="h-4 w-4 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {u.name}
                              {u.is_seed_admin && (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-primary text-primary-foreground rounded-sm">
                                  Inicial
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {u.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-bold bg-secondary capitalize">
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${status.color}`}>
                          <StatusIcon className="h-3.5 w-3.5" />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-3 text-xs">
                          <span className="text-green-700 font-medium">
                            {editCount} editar
                          </span>
                          <span className="text-blue-700 font-medium">
                            {viewCount} ver
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {canEditUsers && !u.is_seed_admin && u.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(u)}
                              title={u.status === 'active' ? 'Desativar' : 'Reativar'}
                              data-testid={`toggle-status-${u.id}`}
                            >
                              {u.status === 'active'
                                ? <ShieldOff className="h-4 w-4" />
                                : <ShieldCheck className="h-4 w-4" />}
                            </Button>
                          )}
                          {canEditUsers && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setResetDialog(u); setResetPasswordValue(''); }}
                              title="Redefinir senha"
                              data-testid={`reset-password-${u.id}`}
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                          )}
                          {canEditUsers && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(u)}
                              title="Editar"
                              data-testid={`edit-usuario-${u.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canEditUsers && !u.is_seed_admin && u.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(u)}
                              title="Excluir"
                              data-testid={`delete-usuario-${u.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-12 text-center text-muted-foreground">
                Nenhum usuário encontrado.
              </div>
            )}
          </div>
          <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
            Mostrando {filtered.length} de {usuarios.length} usuários
          </div>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Editar Usuário' : 'Convidar Novo Usuário'}
              </DialogTitle>
              <DialogDescription>
                {editingUser
                  ? 'Atualize as informações e permissões do usuário'
                  : 'O usuário receberá um e-mail com código para definir a senha e ativar a conta.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      disabled={!!editingUser}
                      data-testid="input-email"
                    />
                  </div>
                  {!editingUser && (
                    <div className="space-y-2 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="user_password">Senha inicial *</Label>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, password: generatePassword() });
                            setShowPassword(true);
                          }}
                          className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                          data-testid="generate-user-password-btn"
                        >
                          <Sparkles className="h-3 w-3" /> Gerar senha forte
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="user_password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          placeholder="Mínimo 8 caracteres"
                          minLength={8}
                          required={!editingUser}
                          autoComplete="new-password"
                          className="pr-10 font-mono"
                          data-testid="input-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-amber-700">
                        ⚠️ Anote esta senha — você precisará informá-la ao usuário.
                      </p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="role">Perfil *</Label>
                    <Select
                      value={formData.role}
                      onValueChange={handleRoleChange}
                    >
                      <SelectTrigger data-testid="select-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {canCreateAdmin && (
                          <SelectItem value="admin">Administrador</SelectItem>
                        )}
                        <SelectItem value="coordenador">Coordenador</SelectItem>
                        <SelectItem value="lideranca">Liderança</SelectItem>
                        <SelectItem value="juridico">Jurídico</SelectItem>
                      </SelectContent>
                    </Select>
                    {!canCreateAdmin && (
                      <p className="text-xs text-muted-foreground">
                        Apenas administradores podem promover outros usuários a admin
                      </p>
                    )}
                  </div>
                  {editingUser && !editingUser.is_seed_admin && (
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(v) =>
                          setFormData({ ...formData, status: v })
                        }
                      >
                        <SelectTrigger data-testid="select-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Ativo</SelectItem>
                          <SelectItem value="disabled">Desativado</SelectItem>
                          {editingUser.status === 'pending' && (
                            <SelectItem value="pending">Pendente</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPermissions(!showPermissions)}
                    className="w-full justify-between"
                    data-testid="toggle-permissions-btn"
                  >
                    <span className="flex items-center gap-2">
                      <UserCog className="h-4 w-4" />
                      {showPermissions ? 'Ocultar' : 'Personalizar'} permissões
                    </span>
                    {showPermissions ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {showPermissions && (
                  <PermissionsEditor
                    permissions={formData.permissions}
                    onChange={(perms) =>
                      setFormData({ ...formData, permissions: perms })
                    }
                    sections={permissionsConfig.sections}
                    roleDefaults={permissionsConfig.role_defaults}
                    currentRole={formData.role}
                  />
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="submit-usuario-btn">
                  {editingUser ? 'Salvar alterações' : 'Criar usuário'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset password dialog */}
        <Dialog open={!!resetDialog} onOpenChange={(o) => !o && setResetDialog(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-indigo-600" />
                Redefinir senha
              </DialogTitle>
              <DialogDescription>
                Defina uma nova senha para <strong>{resetDialog?.name}</strong>. O usuário usará essa senha no próximo login.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Nova senha</Label>
                <button
                  type="button"
                  onClick={() => setResetPasswordValue(generatePassword())}
                  className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                >
                  <Sparkles className="h-3 w-3" /> Gerar senha forte
                </button>
              </div>
              <Input
                type="text"
                value={resetPasswordValue}
                onChange={(e) => setResetPasswordValue(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="font-mono"
                data-testid="reset-password-input"
              />
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠️ Anote esta senha — você precisará informá-la ao usuário.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetDialog(null)}>Cancelar</Button>
              <Button onClick={handleResetPassword} data-testid="confirm-reset-password-btn">
                Redefinir senha
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
